import React, { useEffect, useRef, useState } from "react";
import styles from "./patch-ticket-priority-dropdown.module.module.scss";
import SpinnerWithVariant from "../SpinnerWithVariant/spinner-with-variant";
import { SupportAgentTicket } from "@src/types/support";
import { patchTicketPriorityUseCase } from "@src/cloud-connect/support/use-cases/patch-ticket-priority-use-case";
import {
  errorSnackbar,
  successSnackbar,
} from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import lowPriorityIcon from "@src/assets/svg/low-priority.svg";
import normalPriorityIcon from "@src/assets/svg/normal-priority.svg";
import highPriorityIcon from "@src/assets/svg/high-priority.svg";
import urgentPriorityIcon from "@src/assets/svg/urgent-priority.svg";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import { useI18N } from "@src/language/language-context";
import { useClickOutsideListener } from "@src/hooks/use-click-outside-listener";
import { CustomTooltip } from "@src/components/atoms/custom-tooltip/custom-tooltip";

const cssClassName = "patch-ticket-priority-dropdown";

type PriorityType = {
  code: string;
  label: string;
  icon: string;
};

type PriorityArrayType = PriorityType[];

interface PatchTicketPriorityDropdownPropTypes {
  ticket: SupportAgentTicket;
  disabled?: boolean;
  disabledTooltip?: string;
  // PATCH response returns the updated ticket; propagate so the parent's copy
  // doesn't stay stale until the next SSE refetch
  onTicketUpdated?: (ticket: SupportAgentTicket) => void;
}

export const PatchTicketPriorityDropdown = ({
  ticket,
  disabled = false,
  disabledTooltip = "",
  onTicketUpdated,
}: PatchTicketPriorityDropdownPropTypes) => {
  const i18n = useI18N();

  const texts = {
    labelLow: i18n("modules.patchTicketPriorityDropdown.labelLow"),
    labelNormal: i18n("modules.patchTicketPriorityDropdown.labelNormal"),
    labelHigh: i18n("modules.patchTicketPriorityDropdown.labelHigh"),
    labelUrgent: i18n("modules.patchTicketPriorityDropdown.labelUrgent"),
    errorUpdatingPriority: i18n(
      "modules.patchTicketPriorityDropdown.errorUpdatingPriority",
    ),
    selectPriorityLabel: i18n(
      "modules.patchTicketPriorityDropdown.selectPriorityLabel",
    ),
    successUpdatingPriority: i18n(
      "modules.patchTicketPriorityDropdown.successUpdatingPriority",
    ),
  };

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<PriorityType | null>({
    code: "low",
    label: texts.labelLow,
    icon: lowPriorityIcon,
  });
  const [isChangingPriority, setIsChangingPriority] = useState(false);

  const PRIORITY_ARRAY: PriorityArrayType = [
    { code: "low", label: texts.labelLow, icon: lowPriorityIcon },
    { code: "normal", label: texts.labelNormal, icon: normalPriorityIcon },
    { code: "high", label: texts.labelHigh, icon: highPriorityIcon },
    { code: "urgent", label: texts.labelUrgent, icon: urgentPriorityIcon },
  ];

  const popoverRef = useRef(null);

  useClickOutsideListener(popoverRef, () => setOpen(false), open);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const handleSelect = async (code: string) => {
    if (disabled || isChangingPriority || selected?.code === code) return;
    setIsChangingPriority(true);
    const response = await patchTicketPriorityUseCase(ticket.id, code);

    if (!response.success) {
      errorSnackbar(texts.errorUpdatingPriority);
      setIsChangingPriority(false);
      setOpen(false);
    } else {
      const newSelected =
        PRIORITY_ARRAY.find((priority) => priority.code === code) || null;
      setSelected(newSelected);
      setIsChangingPriority(false);
      setOpen(false);
      successSnackbar(texts.successUpdatingPriority);
      if (onTicketUpdated && response.data) {
        onTicketUpdated(response.data);
      }
    }
  };

  useEffect(() => {
    const currentPriority = PRIORITY_ARRAY.find(
      (priority) => priority.code === ticket.priority,
    ) || { code: "low", label: texts.labelLow, icon: lowPriorityIcon };
    setSelected(currentPriority);
  }, [ticket]);

  return (
    <div ref={popoverRef} className={styles[cssClassName]}>
      <CustomTooltip
        content={disabled ? disabledTooltip : ""}
        className={styles[`${cssClassName}__tooltip-wrapper`]}
      >
        <button
          type="button"
          className={`${styles[`${cssClassName}__selector`]} ${
            disabled && disabledTooltip
              ? styles[`${cssClassName}__selector--sse-disabled`]
              : ""
          }`}
          onClick={() => setOpen(!open)}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open && !disabled}
        >
          {isChangingPriority ? (
            <SpinnerWithVariant variant={["small", "secondary"]} />
          ) : (
            <FlexRowContainer>
              <img
                src={selected ? selected.icon : ""}
                alt={selected ? selected.label : texts.selectPriorityLabel}
                className={styles[`${cssClassName}__icon`]}
              />
              {selected ? selected.label : texts.selectPriorityLabel}
            </FlexRowContainer>
          )}
        </button>
      </CustomTooltip>

      {open && !disabled && (
        <ul
          className={styles[`${cssClassName}__list`]}
          role="listbox"
          aria-activedescendant={selected ? selected.code : undefined}
          tabIndex={0}
        >
          {PRIORITY_ARRAY.map((priority) => (
            <li
              key={priority.code}
              id={priority.code}
              role="option"
              aria-selected={selected ? selected.code === priority.code : false}
            >
              <button
                type="button"
                className={styles[`${cssClassName}__item`]}
                onClick={() => handleSelect(priority.code)}
                disabled={disabled || isChangingPriority}
              >
                <img
                  src={priority.icon}
                  alt={priority.label}
                  className={styles[`${cssClassName}__icon`]}
                />
                {priority.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
